import type { MouseEvent } from 'react'
import React, { createElement, useCallback, useMemo } from 'react'
import { animated, to } from '@react-spring/web'
import { useTooltip } from '@nivo/tooltip'

export const CustomBar = ({
  bar: { data, ...bar },
  barLayout,
  style: {
    borderColor,
    color,
    height,
    labelColor,
    labelOpacity,
    labelX,
    labelY,
    transform,
    width,
  },

  borderRadius,
  borderWidth,

  label,
  shouldRenderLabel,

  isInteractive,
  onClick,
  onMouseEnter,
  onMouseLeave,

  tooltip,
}: // todo fix typing
any) => {
  const { showTooltipFromEvent, hideTooltip } = useTooltip()

  const renderTooltip = useMemo(
    () => () => createElement(tooltip, { ...bar, ...data }),
    [tooltip, bar, data],
  )

  const handleClick = useCallback(
    (event: MouseEvent<SVGRectElement>) => {
      onClick?.({ color: bar.color, ...data }, event)
    },
    [bar, data, onClick],
  )
  const handleTooltip = useCallback(
    (event: MouseEvent<SVGRectElement>) =>
      showTooltipFromEvent(renderTooltip(), event),
    [showTooltipFromEvent, renderTooltip],
  )
  const handleMouseEnter = useCallback(
    (event: MouseEvent<SVGRectElement>) => {
      onMouseEnter?.(data, event)
      showTooltipFromEvent(renderTooltip(), event)
    },
    [data, onMouseEnter, showTooltipFromEvent, renderTooltip],
  )
  const handleMouseLeave = useCallback(
    (event: MouseEvent<SVGRectElement>) => {
      onMouseLeave?.(data, event)
      hideTooltip()
    },
    [data, hideTooltip, onMouseLeave],
  )

  let forceHeight = 0
  let allReviewsBar = false
  if (barLayout === 'ownerReviews') {
    if (bar.key.includes('all.')) {
      forceHeight = 300
      allReviewsBar = true
    }
  }

  // todo if eventually fixed height/width will be the only props which override BarItem component then add it as props and get rid of resolvedDimensions
  const resolveDimensions = useCallback(() => {
    if (!barLayout) {
      return {}
    } else if (barLayout === 'developmentScores') {
      const FIXED_HEIGHT = 10
      const heightDiff = bar.height - FIXED_HEIGHT

      const calculatedY = bar.y + heightDiff / 2

      return {
        newTransform: `translate(${bar.x}, ${calculatedY})`,
        newWidth: bar.width,
        newHeight: FIXED_HEIGHT,
      }
    } else if (barLayout === 'ratingsTypeChart') {
      const FIXED_WIDTH = 4
      const widthDiff = bar.width - FIXED_WIDTH

      const calculatedX = bar.x + widthDiff / 2

      return {
        newTransform: `translate(${calculatedX}, ${bar.y})`,
        newWidth: FIXED_WIDTH,
        newHeight: bar.height,
      }
    } else if (barLayout === 'ownerReviews' && allReviewsBar) {
      return {
        newTransform: `translate(${bar.x}, ${0})`,
        newWidth: bar.width,
        newHeight: bar.height,
      }
    }
    return {}
  }, [bar, barLayout])

  const { newTransform, newWidth, newHeight } = resolveDimensions()

  const rectWidth = to(newWidth ?? width, (value) => Math.max(value, 0))
  const rectHeight = to(newHeight ?? height, (value) =>
    Math.max(forceHeight || value, 0),
  )

  let customLabelY = labelY - 10
  const customLabelX = labelX

  if (barLayout === 'ownerReviews') {
    customLabelY = bar.height - 20
  }

  return (
    <animated.g transform={newTransform ?? transform}>
      <animated.rect
        width={rectWidth}
        height={rectHeight}
        clipPath={
          barLayout === 'ownerReviews'
            ? `url(#owner-reviews-round-corners-${bar.key})`
            : undefined
        }
        rx={borderRadius}
        ry={borderRadius}
        fill={data.fill ?? color}
        strokeWidth={borderWidth}
        stroke={borderColor}
        onMouseEnter={isInteractive ? handleMouseEnter : undefined}
        onMouseMove={isInteractive ? handleTooltip : undefined}
        onMouseLeave={isInteractive ? handleMouseLeave : undefined}
        onClick={isInteractive ? handleClick : undefined}
      />
      {shouldRenderLabel && (
        <animated.text
          x={customLabelX}
          y={customLabelY}
          textAnchor="middle"
          dominantBaseline="central"
          fillOpacity={labelOpacity}
          style={{
            color: '#4E516B',
            fontSize: '14px',
            fontWeight: 500,
            letterSpacing: 0,
            lineHeight: '16px',
            pointerEvents: 'none',
            fill: labelColor,
          }}
        >
          {label}
        </animated.text>
      )}
    </animated.g>
  )
}

export default CustomBar
